import { createAction, createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { ReduxAuthState, SerializedAuth, SetAuthProps, SignInProps, UnauthorizedProps, UserInfo, ResetPasswordProps, ConfirmResetPasswordProps, RegisterProps } from "../types";
import { LS_AUTH_KEY, USER_ROLES, USER_ROLES_ADMINS, USER_ROLES_PORTAL_ADMINS, USER_ROLES_HIDE_PHI, USER_ROLES_ORGANIZATION_BILLING_ADMIN } from "../../shared/constants";
import jwtDecode from 'jwt-decode';
import { ReduxSliceState } from "../../shared/types";
import { ReduxRootState } from "../../shared/redux/store";

export const login = createAction('login');

const initialState: ReduxAuthState = {
    signInState: 'READY',
    resetPasswordState: 'READY',
    resetPasswordConfirmState: 'READY',
    userInfo: undefined,
    token: undefined,
    refreshToken: undefined,
};

function withPayloadType<T>() {
    return (t: T) => ({ payload: t })
}

export const passwordReset = createAction('auth/passwordreset/request', withPayloadType<ResetPasswordProps>());
export const confirmPasswordReset = createAction('auth/passwordreset/confirm', withPayloadType<ConfirmResetPasswordProps>());
export const signIn = createAction('signin', withPayloadType<SignInProps>());
export const unauthorized = createAction('auth/unauthorized', withPayloadType<UnauthorizedProps>());
export const register = createAction('auth/register', withPayloadType<RegisterProps>());
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setSignInState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.signInState = `${action.payload}`;
        },
        setResetPasswordState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.resetPasswordState = action.payload;
        },
        setResetPasswordConfirmState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.resetPasswordConfirmState = action.payload;
        },
        setAuth: (state, action: PayloadAction<SetAuthProps>) => {
            const { token, refreshToken } = action.payload;
            const userInfo = jwtDecode<UserInfo>(token);

            state.token = token;
            state.refreshToken = refreshToken;
            state.userInfo = {...userInfo};

            localStorage.setItem(LS_AUTH_KEY, JSON.stringify({
                token,
                refreshToken,
                userInfo,
            }));
        },

        rehydrateAuth: (state) => {
            const lsAuth = localStorage.getItem(LS_AUTH_KEY);

            //No auth stored in local storage
            if(lsAuth === null || lsAuth === '')
                return;

            const { token, refreshToken, userInfo } = JSON.parse(lsAuth) as SerializedAuth;
            state.token = token;
            state.refreshToken = refreshToken;
            state.userInfo = userInfo;
        },

        clearAuth: (state) => {
            state.token = undefined;
            state.refreshToken = undefined;
            state.userInfo = undefined;

            localStorage.removeItem(LS_AUTH_KEY);
        }
    }
});

const selectSelf = (state: ReduxRootState) => state.auth;
export const selectUserInfo = createSelector(selectSelf, self => self.userInfo);
export const selectIsAuthenticated = createSelector(selectSelf, state => state.token !== undefined);
export const selectCurrentUser = createSelector(selectSelf, state => state.userInfo);
export const selectCurrentUserRole = createSelector(selectSelf, state => state.userInfo?.rolename);
export const selectCurrentUserIsAdmin = createSelector(selectCurrentUserRole, role => USER_ROLES_ADMINS.some(x => x === role));
export const selectCurrentUserIsPortalAdmin = createSelector(selectCurrentUserRole, role => USER_ROLES_PORTAL_ADMINS.some(x => x === role));
export const selectCurrentUserIsOrganizationAdmin = createSelector(selectCurrentUserRole, role => USER_ROLES_ORGANIZATION_BILLING_ADMIN.some(x => x === role));
export const selectCurrentUserViewSensitive = createSelector(selectCurrentUserRole, role => !USER_ROLES_HIDE_PHI.some(x => x === role));
export const selectUserFullName = createSelector(selectCurrentUser, state => `${state?.repname}`);
// Aug 2026 - capped at 2 chars (was unbounded) - a repname with more than two words (e.g. an
// Enterprise Billing pseudo-user's longer display name) overflowed the round avatar chip instead
// of wrapping/truncating. Matches Account.tsx's own initials logic.
export const selectUserAvatarName = createSelector(selectUserFullName, name =>
    name.trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('').toUpperCase()
);
export const selectUserOrgId = createSelector(selectUserInfo, userInfo => userInfo?.orgid);
export const selectUserOrgName = createSelector(selectUserInfo, userInfo => userInfo?.orgname);
export const selectUserEmail = createSelector(selectUserInfo, userInfo => userInfo?.email);
export const selectUserRole = createSelector(selectUserInfo, userInfo => userInfo?.rolename);
export const selectIsSignInState = createSelector(selectSelf, state => state.signInState);
export const selectIsSignInBusy = createSelector(selectIsSignInState, state => state === 'BUSY');
export const selectIdToken = createSelector(selectSelf, state => state.token);
// Sep 2026 - refresh-token flow - read directly by httpClient.ts's silent-refresh interceptor via
// `store.getState()` (not a hook - that module isn't a React component), but exported as a proper
// selector for consistency with the rest of this file and easier testing.
export const selectRefreshToken = createSelector(selectSelf, state => state.refreshToken);

export const selectResetPasswordState = createSelector(selectSelf, self => self.resetPasswordState);
export const selectResetPasswordIsBusy = createSelector(selectResetPasswordState, state => state === 'BUSY');

export const selectResetPasswordConfirmState = createSelector(selectSelf, self => self.resetPasswordState);
export const selectResetPasswordConfirmIsBusy = createSelector(selectResetPasswordConfirmState, state => state === 'BUSY');

export default authSlice;
